
import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PuttSituation } from "@/types/golf";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import ScoreButtons from "./approaches/ScoreButtons";
import ScoreTable from "./approaches/ScoreTable";

interface ApproachTestProps {
  situation: PuttSituation;
  onClose: () => void;
  refreshAfterToast?: () => void;
}

const ApproachTest = ({ situation, onClose, refreshAfterToast }: ApproachTestProps) => {
  const { t } = useTranslation('training');
  const [scores, setScores] = useState<Record<string, number[]>>({});
  const [currentAttempt, setCurrentAttempt] = useState<number>(0);
  const currentPoint = situation.distances[0].point;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Map approach IDs to translation keys
  const translationKeyMap: Record<number, string> = {
    7: "liftedApproaches",
    8: "longChips",
    9: "shortChips",
    10: "wedging50m",
    11: "wedging30m",
    12: "shortBunker",
    13: "longBunker",
    14: "midDistanceApproaches",
    15: "roughApproaches",
  };
  
  const exerciseKey = translationKeyMap[situation.id];
  const exerciseName = exerciseKey ? t(`approaches.exercises.${exerciseKey}.name`) : situation.name;

  const handleScore = (points: number) => {
    setScores((prev) => ({
      ...prev,
      [currentPoint]: [...(prev[currentPoint] || []), points],
    }));

    const maxAttempts = situation.distances[0].ballCount;
    if (currentAttempt < maxAttempts - 1) {
      setCurrentAttempt(currentAttempt + 1);
    }
  };

  const undoLastScore = () => {
    setScores((prev) => {
      const newScores = { ...prev };
      if (newScores[currentPoint]?.length) {
        newScores[currentPoint] = newScores[currentPoint].slice(0, -1);
        if (newScores[currentPoint].length === 0) {
          delete newScores[currentPoint];
        }
        if (currentAttempt > 0) {
          setCurrentAttempt(currentAttempt - 1);
        }
      }
      return newScores;
    });
  };

  const getPointTotal = () => {
    const pointScores = scores[currentPoint] || [];
    return pointScores.reduce((sum, score) => sum + score, 0);
  };

  const totalPoints = getPointTotal();
  
  // Calculer l'index en prenant la meilleure valeur disponible
  const calculateIndex = (points: number): number => {
    // Parcourir la table du plus haut score au plus bas
    for (let i = situation.indexTable.length - 1; i >= 0; i--) {
      if (points >= situation.indexTable[i].points) {
        return situation.indexTable[i].index;
      }
    }
    // Si le score est inférieur au minimum, retourner le premier index
    return situation.indexTable[0]?.index ?? 0;
  };
  
  const currentIndex = calculateIndex(totalPoints);
  const maxAttempts = situation.distances[0].ballCount;
  const isTestComplete = (scores[currentPoint]?.length || 0) === maxAttempts;

  const handleClose = async () => {
    if (Object.keys(scores).length === 0) {
      onClose();
      return;
    }

    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      toast({
        variant: "destructive",
        title: t('approaches.interface.error'),
        description: t('approaches.interface.mustBeLoggedIn'),
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }
      
      return;
    }

    try {
      const { error } = await supabase
        .from('putting_test_results')
        .insert({
          user_id: session.session.user.id,
          situation_id: situation.id,
          points: totalPoints,
          index: currentIndex,
        });

      if (error) throw error;

      toast({
        title: t('approaches.interface.testSaved'),
        description: t('approaches.interface.resultsSaved'),
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }

      queryClient.invalidateQueries({ queryKey: ["putting-results"] });
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('approaches.interface.error'),
        description: t('approaches.interface.saveError'),
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }
    }
  };

  return (
    <Card className="w-full max-w-xl mx-auto mt-2 md:mt-6">
      <CardHeader className="space-y-2">
        <div className="flex justify-between items-center mb-2">
          <Button 
            variant="outline" 
            asChild
          >
            <a href="/approaches" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {t('approaches.interface.backToTest')}
            </a>
          </Button>
        </div>
        <CardTitle className="text-3xl font-bold text-green-700 text-center">
          {exerciseName}
        </CardTitle>
        <p className="text-xl text-green-600 font-medium">
          {t('approaches.interface.distance')}: {situation.distances[0].distance}m
        </p>
        <h3 className="text-xl font-medium">
          {t('approaches.interface.point')} {currentPoint} - {t('approaches.interface.attempt')} {currentAttempt + 1}/{maxAttempts}
        </h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScoreButtons 
          situation={situation} 
          onScoreSelect={handleScore} 
          isTestComplete={isTestComplete} 
        />
        
        <Button
          onClick={undoLastScore}
          disabled={!scores[currentPoint]?.length}
          className="h-14 font-medium bg-red-200 hover:bg-red-300 text-red-700 border-0 w-full mt-2"
        >
          {t('approaches.interface.undoLastEntry')}
        </Button>

        <ScoreTable 
          situation={situation}
          scores={scores}
          currentPoint={currentPoint}
          maxAttempts={maxAttempts}
          totalPoints={totalPoints}
          currentIndex={currentIndex}
        />

        <div className="flex justify-end mt-8">
          <Button 
            onClick={handleClose} 
            variant="outline"
            className="px-8 py-2 text-lg bg-white hover:bg-gray-100 text-golf-700 border-golf-600"
          >
            {t('approaches.interface.finish')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApproachTest;
